import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store';
import { logout } from '../../../store/slices/authSlice';
import * as S from './styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSignOutAlt,
  faSearch,
  faBuilding,
  faBook,
  faUser,
  faClipboardList,
  faChartPie,
  faAddressBook,
  faFileInvoiceDollar,
  faDatabase,
  faMailForward,
} from '@fortawesome/free-solid-svg-icons';
import Logo from '../Logo';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../../../services/api';
interface SidebarProps {
  expanded: boolean;
}

interface MenuItem {
  id: string;
  icon: any;
  label: string;
  path: string;
  requiredGroup?: number;
  subItems?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    id: 'cadastro',
    icon: faBook,
    label: 'Cadastros',
    path: '/cadastros',
    subItems: [
      {
        id: 'cadastro-usuarios',
        icon: faUser,
        label: 'Usuários',
        path: '/cadastros/usuario',
      },
      {
        id: 'cadastro-ocorrencias',
        icon: faFileInvoiceDollar,
        label: 'Ocorrências',
        path: '/cadastros/ocorrencia',
      },
      {
        id: 'cadastro-notas',
        icon: faClipboardList,
        label: 'Notas',
        path: '/cadastros/notas',
      },
    ],
  },
  {
    id: 'relatorios',
    icon: faChartPie,
    label: 'Relatórios',
    path: '/relatorios',
    subItems: [
      {
        id: 'relatorios-entregas',
        icon: faChartPie,
        label: 'Entregas',
        path: '/relatorios/entregas',
      },
      {
        id: 'relatorios-entregadores',
        icon: faMailForward,
        label: 'Entregadores',
        path: '/relatorios/entregadores',
      },
      {
        id: 'relatorios-despesas',
        icon: faFileInvoiceDollar,
        label: 'Despesas',
        path: '/relatorios/despesas',
      }
    ],
  },
  {
    id: 'documentacao',
    icon: faAddressBook,
    label: 'Documentação',
    path: '/documentacao',
    requiredGroup: 1,
  },
];

const ambientes = [
  'Desenvolvimento',
  'Homologação',
  'Produção'
]


const Sidebar: React.FC<SidebarProps> = ({ expanded }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMenuItems, setFilteredMenuItems] = useState<MenuItem[]>([]);
  const [empresa, setEmpresa] = useState<string | null>(null);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(
    {}
  );
  const [hoveredMenuItem, setHoveredMenuItem] = useState<string | null>(null);
  const [menuItemPosition, setMenuItemPosition] = useState<{
    top: number;
    id: string;
  } | null>(null);

  const availableMenuItems = useMemo(() => {
    return menuItems.filter((item) => {
      if (!item.requiredGroup) return true;
      return user && user.codigoGrupoUsuario === item.requiredGroup;
    });
  }, [user]);

  useEffect(() => {
    setFilteredMenuItems(availableMenuItems);
  }, [availableMenuItems]);

  useEffect(() => setEmpresa(user.empresa), [user]);

  const empresaDisplay = useMemo(() => {
    if (!empresa) return 'Nenhuma empresa selecionada';
    return empresa.charAt(0).toUpperCase() + empresa.slice(1);
  }, [empresa]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredMenuItems(availableMenuItems);
      return;
    }

    const filtered = availableMenuItems.filter(
      (item) =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.subItems?.some((subItem) =>
          subItem.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );
    setFilteredMenuItems(filtered);
  }, [searchTerm, availableMenuItems]);

  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const toggleSubMenu = useCallback((itemId: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  }, []);

  const isItemActive = useCallback(
    (item: MenuItem) => {
      if (location.pathname === item.path) return true;
      if (item.subItems) {
        return item.subItems.some(
          (subItem) => location.pathname === subItem.path
        );
      }
      return false;
    },
    [location.pathname]
  );

  useEffect(() => {
    const newExpandedMenus: Record<string, boolean> = {};

    menuItems.forEach((item) => {
      if (
        item.subItems &&
        item.subItems.some((subItem) => location.pathname === subItem.path)
      ) {
        newExpandedMenus[item.id] = true;
      }
    });

    setExpandedMenus((prev) => ({
      ...prev,
      ...newExpandedMenus,
    }));
  }, [location.pathname]);

  const handleMouseEnter = useCallback(
    (itemId: string, event: React.MouseEvent<HTMLLIElement>) => {
      setHoveredMenuItem(itemId);
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuItemPosition({ top: rect.top, id: itemId });
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredMenuItem(null);
    setMenuItemPosition(null);
  }, []);

  return (
    <S.SidebarContainer expanded={expanded}>
      <S.SidebarHeader>
        <Logo size={expanded ? 'medium' : 'small'} />
      </S.SidebarHeader>

      <S.SidebarBody>
        {expanded && (
          <S.SearchContainer>
            <S.SearchInput
              type="text"
              placeholder="Pesquisar menu"
              value={searchTerm}
              onChange={handleSearch}
            />
            <S.SearchIcon>
              <FontAwesomeIcon icon={faSearch} />
            </S.SearchIcon>
          </S.SearchContainer>
        )}

        <S.MenuList>
          {filteredMenuItems.map((item) => (
            <React.Fragment key={item.id}>
              <S.MenuItem
                onMouseEnter={(e) =>
                  item.subItems && handleMouseEnter(item.id, e)
                }
                onMouseLeave={handleMouseLeave}
                onClick={() =>
                  item.subItems && expanded ? toggleSubMenu(item.id) : null
                }
              >
                {item.subItems ? (
                  <S.MenuItemButton
                    className={isItemActive(item) ? 'active' : ''}
                  >
                    <S.MenuItemIcon>
                      <FontAwesomeIcon icon={item.icon} />
                    </S.MenuItemIcon>
                    {expanded && (
                      <>
                        <span>{item.label}</span>
                        <S.MenuItemArrow
                          expanded={expandedMenus[item.id] || false}
                        />
                      </>
                    )}
                  </S.MenuItemButton>
                ) : (
                  <S.MenuItemLink
                    as={Link}
                    to={item.path}
                    className={location.pathname === item.path ? 'active' : ''}
                  >
                    <S.MenuItemIcon>
                      <FontAwesomeIcon icon={item.icon} />
                    </S.MenuItemIcon>
                    {expanded && <span>{item.label}</span>}
                  </S.MenuItemLink>
                )}

                {item.subItems &&
                  !expanded &&
                  hoveredMenuItem === item.id &&
                  menuItemPosition?.id === item.id && (
                    <S.FloatingSubMenu
                      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) =>
                        handleMouseEnter(
                          item.id,
                          e as unknown as React.MouseEvent<HTMLLIElement>
                        )
                      }
                      onMouseLeave={handleMouseLeave}
                      top={menuItemPosition.top}
                    >
                      {item.subItems.map((subItem) => (
                        <S.SubMenuItem key={subItem.id}>
                          <S.SubMenuItemLink
                            as={Link}
                            to={subItem.path}
                            className={
                              location.pathname === subItem.path ? 'active' : ''
                            }
                          >
                            <S.SubMenuItemIcon>
                              <FontAwesomeIcon icon={subItem.icon} />
                            </S.SubMenuItemIcon>
                            <span>{subItem.label}</span>
                          </S.SubMenuItemLink>
                        </S.SubMenuItem>
                      ))}
                    </S.FloatingSubMenu>
                  )}
              </S.MenuItem>

              {item.subItems && expandedMenus[item.id] && expanded && (
                <S.SubMenuList>
                  {item.subItems.map((subItem) => (
                    <S.SubMenuItem key={subItem.id}>
                      <S.SubMenuItemLink
                        as={Link}
                        to={subItem.path}
                        className={
                          location.pathname === subItem.path ? 'active' : ''
                        }
                      >
                        <S.SubMenuItemIcon>
                          <FontAwesomeIcon icon={subItem.icon} />
                        </S.SubMenuItemIcon>
                        <span>{subItem.label}</span>
                      </S.SubMenuItemLink>
                    </S.SubMenuItem>
                  ))}
                </S.SubMenuList>
              )}
            </React.Fragment>
          ))}
        </S.MenuList>

        <S.LogoutButton onClick={handleLogout}>
          <S.MenuLogoutIcon>
            <FontAwesomeIcon icon={faSignOutAlt} />
          </S.MenuLogoutIcon>
          {expanded && <span>Sair</span>}
        </S.LogoutButton>

        {user.codigoGrupoUsuario == 1 ? (
          <S.DataBase dataBase={api.host.split('://')[1]}>
            <S.DataBaseIcon>
              <FontAwesomeIcon icon={faDatabase} />
            </S.DataBaseIcon>
            {
              expanded &&(
                <>
                {
                api.host.split('://')[1]
                .includes('ada')? ambientes[2] :
                api.host.split('://')[1]
                .includes('altinfo')? ambientes[1] :
                ambientes[0]
                }
                </>
              )
            }
          </S.DataBase>
        ) : null}

        <S.Empresa>
          <S.EmpresaIcon>
            <FontAwesomeIcon icon={faBuilding} />
          </S.EmpresaIcon>
          {expanded && <S.EmpresaText>{empresaDisplay}</S.EmpresaText>}
        </S.Empresa>
      </S.SidebarBody>
    </S.SidebarContainer>
  );
};

export default React.memo(Sidebar);
